/**
 * FavoritesAndRecent — favorites + recently-used templates rendered above the
 * grouped Template sections on the Blueprints Templates tab.
 *
 * ## Collections
 *
 *   1. **Your favorites** — entity refs in the user's starred-entities set
 *      that resolve to kind:Template entities. Capped at 6 cards.
 *   2. **Recently used** — the last 5 distinct template refs found in the
 *      current user's task history, resolved to Template entities. Capped
 *      at 5 cards.
 *
 * ## Layout by view mode
 *
 *   - **Grid view** — the two collections are merged into a single
 *     "Favorites & recently used" section: favorites first, then recently-used
 *     items that aren't already favorites (de-duplicated by catalog ref).
 *   - **List view** — kept as two separate compact rows ("Your favorites" /
 *     "Recently used").
 *
 * ## Failure isolation
 *
 * Each row runs its own independent effect with its own loading / error state.
 * An error in one row hides that row and MUST NOT propagate to the other or
 * to the Templates grid below. Errors are logged to console but swallowed.
 *
 * ## API note — getEntitiesByRefs
 *
 * `catalogApi.getEntitiesByRefs` is confirmed available in
 * `@backstage/catalog-client` (interface CatalogApi, line 545 of index.d.ts).
 * It returns `{ items: Array<Entity | undefined> }` with undefined for refs
 * that weren't found. We filter those out before rendering.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Box, Flex, Grid, Skeleton, Text } from '@backstage/ui';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { useNavigate } from 'react-router-dom';
import type { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { BlueprintCard, BlueprintListRow } from './BlueprintCard';

// ── Layout ────────────────────────────────────────────────────────────────────

/**
 * A section heading underlined in the brand colour, matching the
 * curated-category section headers on the Templates tab.
 */
function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Flex
      align="center"
      py="2"
      mb="4"
      style={{ borderBottom: '2px solid var(--make-fg-brand)' }}
    >
      <Text as="h2" variant="body-large" weight="bold">
        {children}
      </Text>
    </Flex>
  );
}

/** Indeterminate loading bar on BUI's animated Skeleton. */
function LoadingRow({ label }: { label: string }) {
  return (
    <Box role="progressbar" aria-label={label} mb="6">
      <Skeleton width="100%" height={4} rounded />
    </Box>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isTemplateEntity(entity: unknown): entity is TemplateEntityV1beta3 {
  return (
    typeof entity === 'object' &&
    entity !== null &&
    (entity as { kind?: string }).kind === 'Template'
  );
}

/** Stable catalog ref key (`namespace/name`) used for de-duplication. */
function templateKey(t: TemplateEntityV1beta3): string {
  return `${t.metadata.namespace ?? 'default'}/${t.metadata.name}`;
}

// ── Shared row layout ─────────────────────────────────────────────────────────

interface RowProps {
  title: string;
  templates: TemplateEntityV1beta3[];
  viewMode: 'grid' | 'list';
  onChoose: (template: TemplateEntityV1beta3) => void;
}

function TemplateRow({
  title,
  templates,
  viewMode,
  onChoose,
}: RowProps): JSX.Element {
  return (
    <Box
      mb="6"
      data-testid={`blueprints-row-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <SectionHeading>{title}</SectionHeading>
      {viewMode === 'grid' ? (
        <Grid.Root
          columns={{ initial: '1', sm: '2', md: '3', lg: '4' }}
          gap="4"
        >
          {templates.map(t => (
            <BlueprintCard
              key={templateKey(t)}
              template={t}
              onChoose={() => onChoose(t)}
            />
          ))}
        </Grid.Root>
      ) : (
        <Flex direction="column" gap="2">
          {templates.map(t => (
            <BlueprintListRow
              key={templateKey(t)}
              template={t}
              onChoose={() => onChoose(t)}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Renders "Your favorites" and "Recently used" rows above the curated-category
 * template sections. Each row loads and fails independently.
 *
 * @param viewMode - Inherits the parent Templates page's grid/list toggle so
 *   favorites and recently-used render in the same mode as the sections below.
 */
export default function FavoritesAndRecent({
  viewMode = 'grid',
}: {
  viewMode?: 'grid' | 'list';
} = {}): JSX.Element {
  const catalogApi = useApi(catalogApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const navigate = useNavigate();
  const selectedTemplateRoute = useRouteRef(
    scaffolderPlugin.routes.selectedTemplate,
  );

  // Shared navigation handler — passed as `onChoose` to every card.
  const handleChoose = useCallback(
    (template: TemplateEntityV1beta3) => {
      navigate(
        selectedTemplateRoute({
          namespace: template.metadata.namespace ?? 'default',
          templateName: template.metadata.name,
        }),
      );
    },
    [navigate, selectedTemplateRoute],
  );

  // ── Favorites row ─────────────────────────────────────────────────────────

  const { starredEntities } = useStarredEntities();

  /**
   * Stable serialized key derived from the starred-entities Set.
   *
   * `starredEntities` is a new Set reference on every observable emission,
   * so we can't use it directly as a useEffect dependency without risking
   * spurious re-runs. Instead, we derive a deterministic sorted string and
   * depend on that. The effect only re-runs when the content actually changes.
   *
   * We restrict to refs that start with "template:" (case-insensitive) to
   * avoid fetching non-template entities; we later re-check `kind === 'Template'`
   * on the resolved entities as a safety net.
   */
  const templateStarredKey = useMemo(
    () =>
      Array.from(starredEntities)
        .filter(r => r.toLowerCase().startsWith('template:'))
        .sort()
        .join('\0'),
    [starredEntities],
  );

  const [favLoading, setFavLoading] = useState(true);
  const [favTemplates, setFavTemplates] = useState<TemplateEntityV1beta3[]>([]);

  useEffect(() => {
    const refs = templateStarredKey ? templateStarredKey.split('\0') : [];

    if (refs.length === 0) {
      setFavTemplates([]);
      setFavLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFavLoading(true);

    // Cap to 6 before fetching — avoids retrieving entities we will not display.
    catalogApi
      .getEntitiesByRefs({ entityRefs: refs.slice(0, 6) })
      .then(({ items }) => {
        if (cancelled) return;
        // Keep the render cap as a defensive guard in case an API mock or
        // implementation returns more entities than requested.
        const resolved = items.filter(isTemplateEntity).slice(0, 6);
        setFavTemplates(resolved);
        setFavLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        // oxlint-disable-next-line no-console
        console.error(
          '[Blueprints] FavoritesAndRecent — favorites row failed:',
          err,
        );
        // Row hides (templates stays empty), page continues to function.
        setFavTemplates([]);
        setFavLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [templateStarredKey, catalogApi]);

  // ── Recently used row ─────────────────────────────────────────────────────

  const [recentLoading, setRecentLoading] = useState(true);
  const [recentTemplates, setRecentTemplates] = useState<
    TemplateEntityV1beta3[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    setRecentLoading(true);

    scaffolderApi
      .listTasks({ filterByOwnership: 'owned', limit: 50 })
      .then(({ tasks }) => {
        if (cancelled) return;

        // Sort by createdAt descending so the most recent task wins when a
        // template ref appears more than once. The API may or may not guarantee
        // order, so we sort explicitly.
        const sorted = [...tasks].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        // Derive distinct template refs, preserving recency order.
        // A ref that appears at indices [0, 3, 5] is kept at position 0.
        const seen = new Set<string>();
        const distinctRefs: string[] = [];
        for (const task of sorted) {
          const ref = task.spec.templateInfo?.entityRef;
          if (!ref || seen.has(ref)) continue;
          seen.add(ref);
          distinctRefs.push(ref);
          if (distinctRefs.length >= 5) break; // cap at 5 distinct refs
        }

        if (distinctRefs.length === 0) {
          setRecentTemplates([]);
          setRecentLoading(false);
          return;
        }

        catalogApi
          .getEntitiesByRefs({ entityRefs: distinctRefs })
          .then(({ items }) => {
            if (cancelled) return;
            const resolved = items.filter(isTemplateEntity);
            setRecentTemplates(resolved);
            setRecentLoading(false);
          })
          .catch(innerErr => {
            if (cancelled) return;
            // oxlint-disable-next-line no-console
            console.error(
              '[Blueprints] FavoritesAndRecent — recently used catalog resolve failed:',
              innerErr,
            );
            setRecentTemplates([]);
            setRecentLoading(false);
          });
      })
      .catch(err => {
        if (cancelled) return;
        // oxlint-disable-next-line no-console
        console.error(
          '[Blueprints] FavoritesAndRecent — recently used task fetch failed:',
          err,
        );
        setRecentTemplates([]);
        setRecentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scaffolderApi, catalogApi]);

  // ── Render ────────────────────────────────────────────────────────────────

  // Nothing to show at all — return empty fragment so the parent layout
  // doesn't render any wasted whitespace.
  const favVisible = !favLoading && favTemplates.length > 0;
  const recentVisible = !recentLoading && recentTemplates.length > 0;
  const anyLoading = favLoading || recentLoading;

  if (!anyLoading && !favVisible && !recentVisible) {
    return <></>;
  }

  // In grid view the two collections are merged into a single section —
  // favorites first, then recently-used items that aren't already favorites
  // (de-duplicated by catalog ref). In list view they stay as two separate
  // rows so each keeps its own compact heading.
  if (viewMode === 'grid') {
    const favKeys = new Set(favTemplates.map(templateKey));
    const combined = [
      ...favTemplates,
      ...recentTemplates.filter(t => !favKeys.has(templateKey(t))),
    ];

    return (
      <div data-testid="favorites-and-recent">
        {/*
         * Render the combined section as soon as either collection has
         * resolved — favorites and recents load independently, so we must not
         * hide already-loaded cards behind a spinner while the other request
         * is still in flight. The spinner only shows while nothing has loaded
         * yet.
         */}
        {combined.length > 0 && (
          <TemplateRow
            title="Favorites & recently used"
            templates={combined}
            viewMode={viewMode}
            onChoose={handleChoose}
          />
        )}
        {anyLoading && combined.length === 0 && (
          <LoadingRow label="Loading favorites and recently used templates" />
        )}
      </div>
    );
  }

  return (
    // The outer div gives the two rows a shared top-level container that
    // the parent (BlueprintsTemplateListPage) can target in tests.
    <div data-testid="favorites-and-recent">
      {/* Favorites row */}
      {favLoading && <LoadingRow label="Loading favorite templates" />}
      {favVisible && (
        <TemplateRow
          title="Your favorites"
          templates={favTemplates}
          viewMode={viewMode}
          onChoose={handleChoose}
        />
      )}

      {/* Recently used row */}
      {recentLoading && <LoadingRow label="Loading recently used templates" />}
      {recentVisible && (
        <TemplateRow
          title="Recently used"
          templates={recentTemplates}
          viewMode={viewMode}
          onChoose={handleChoose}
        />
      )}
    </div>
  );
}
